import { type SupportedLocale, SUPPORTED_LOCALES_ARRAY } from "../i18n-locales";

// Route keys are stable internal identifiers. Each maps to a localized slug
// per language so permalinks use real words in the visitor's language
// (e.g. /it/servizi, /en/services, /es/servicios).
export type RouteKey =
  | "home"
  | "about"
  | "careers"
  | "services"
  | "tools"
  | "editorial"
  | "sponsored"
  | "portfolio"
  | "partners"
  | "blog"
  | "shop"
  | "contact"
  | "faq"
  | "inquiries"
  | "search"
  | "privacy"
  | "cookie"
  | "terms"
  | "withdrawal";

// Empty string = the locale index route ("/it").
export const ROUTE_SLUGS: Record<RouteKey, Record<SupportedLocale, string>> = {
  home: { it: "", en: "", es: "", fr: "", de: "" },
  about: {
    it: "chi-siamo",
    en: "about",
    es: "quienes-somos",
    fr: "a-propos",
    de: "ueber-uns",
  },
  careers: {
    it: "lavora-con-noi",
    en: "careers",
    es: "trabaja-con-nosotros",
    fr: "nous-rejoindre",
    de: "karriere",
  },
  services: {
    it: "servizi",
    en: "services",
    es: "servicios",
    fr: "services",
    de: "leistungen",
  },
  tools: {
    it: "strumenti",
    en: "tools",
    es: "herramientas",
    fr: "outils",
    de: "werkzeuge",
  },
  editorial: {
    it: "piano-editoriale",
    en: "editorial-plan",
    es: "plan-editorial",
    fr: "plan-editorial",
    de: "redaktionsplan",
  },
  sponsored: {
    it: "articoli-sponsorizzati",
    en: "sponsored-articles",
    es: "articulos-patrocinados",
    fr: "articles-sponsorises",
    de: "gesponserte-artikel",
  },
  portfolio: {
    it: "portfolio",
    en: "portfolio",
    es: "portafolio",
    fr: "portfolio",
    de: "portfolio",
  },
  partners: {
    it: "partner",
    en: "partners",
    es: "socios",
    fr: "partenaires",
    de: "partner",
  },
  blog: {
    it: "magazine",
    en: "magazine",
    es: "revista",
    fr: "magazine",
    de: "magazin",
  },
  shop: {
    it: "negozio",
    en: "shop",
    es: "tienda",
    fr: "boutique",
    de: "shop",
  },
  contact: {
    it: "supporto",
    en: "support",
    es: "soporte",
    fr: "support",
    de: "support",
  },
  faq: {
    it: "faq",
    en: "faq",
    es: "preguntas-frecuentes",
    fr: "questions-frequentes",
    de: "haeufige-fragen",
  },
  inquiries: {
    it: "contatti-preventivi",
    en: "contact-quotes",
    es: "contacto-presupuestos",
    fr: "contact-devis",
    de: "kontakt-angebote",
  },
  search: {
    it: "cerca",
    en: "search",
    es: "buscar",
    fr: "recherche",
    de: "suche",
  },
  privacy: {
    it: "privacy",
    en: "privacy",
    es: "privacidad",
    fr: "confidentialite",
    de: "datenschutz",
  },
  cookie: {
    it: "cookie",
    en: "cookie",
    es: "cookie",
    fr: "cookie",
    de: "cookie",
  },
  terms: {
    it: "termini-condizioni",
    en: "terms-conditions",
    es: "terminos-condiciones",
    fr: "conditions-generales",
    de: "allgemeine-bedingungen",
  },
  withdrawal: {
    it: "recesso",
    en: "withdrawal",
    es: "desistimiento",
    fr: "retractation",
    de: "widerruf",
  },
};

// Service detail keys. Each has a localized sub-slug used under the services
// route, e.g. /it/servizi/siti-web, /en/services/websites.
export type ServiceKey =
  | "web"
  | "webapp"
  | "software"
  | "ecommerce"
  | "marketing"
  | "seo"
  | "graphics"
  | "printing"
  | "coworking"
  | "wpplugin"
  | "psmodule"
  | "wptheme"
  | "tessidra";

export const SERVICE_KEYS: ServiceKey[] = [
  "web",
  "webapp",
  "software",
  "ecommerce",
  "marketing",
  "seo",
  "graphics",
  "printing",
  "coworking",
  "wpplugin",
  "psmodule",
  "wptheme",
  "tessidra",
];

export const SERVICE_SLUGS: Record<
  ServiceKey,
  Record<SupportedLocale, string>
> = {
  web: {
    it: "siti-web",
    en: "websites",
    es: "sitios-web",
    fr: "sites-web",
    de: "webseiten",
  },
  software: {it:"sviluppo-software",en:"software-development",es:"desarrollo-software",fr:"developpement-logiciel",de:"softwareentwicklung"},
  webapp: {
    it: "web-app",
    en: "web-apps",
    es: "aplicaciones-web",
    fr: "applications-web",
    de: "web-apps",
  },
  ecommerce: {
    it: "e-commerce",
    en: "e-commerce",
    es: "e-commerce",
    fr: "e-commerce",
    de: "e-commerce",
  },
  marketing: {
    it: "social-marketing",
    en: "social-marketing",
    es: "marketing-social",
    fr: "marketing-social",
    de: "social-marketing",
  },
  seo: {
    it: "seo-google",
    en: "seo-google",
    es: "seo-google",
    fr: "seo-google",
    de: "seo-google",
  },
  graphics: {
    it: "grafica",
    en: "graphic-design",
    es: "diseno-grafico",
    fr: "design-graphique",
    de: "grafikdesign",
  },
  printing: {
    it: "stampa-digitale",
    en: "digital-printing",
    es: "impresion-digital",
    fr: "impression-numerique",
    de: "digitaldruck",
  },
  coworking: {
    it: "coworking",
    en: "coworking",
    es: "coworking",
    fr: "coworking",
    de: "coworking",
  },
  wpplugin: {
    it: "plugin-wordpress",
    en: "wordpress-plugins",
    es: "plugins-wordpress",
    fr: "plugins-wordpress",
    de: "wordpress-plugins",
  },
  psmodule: {
    it: "moduli-prestashop",
    en: "prestashop-modules",
    es: "modulos-prestashop",
    fr: "modules-prestashop",
    de: "prestashop-module",
  },
  wptheme: {
    it: "template-wordpress",
    en: "wordpress-themes",
    es: "plantillas-wordpress",
    fr: "themes-wordpress",
    de: "wordpress-templates",
  },
  tessidra: {
    it: "tessidra",
    en: "tessidra",
    es: "tessidra",
    fr: "tessidra",
    de: "tessidra",
  },
};

// Build a localized href for a route key in a given locale.
export function localizedPath(key: RouteKey, locale: string): string {
  const loc = (SUPPORTED_LOCALES_ARRAY as string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  const slug = ROUTE_SLUGS[key][loc];
  return slug ? `/${loc}/${slug}` : `/${loc}`;
}

export type InquiryLinkOptions = {
  serviceKey?: string;
  offerVariant?: "classic" | "ai" | "pro";
  area?: "editorial" | "sponsored" | "partnership" | "general";
  requestType?: string;
};

// Build a deep link to the commercial enquiry form. Service detail pages pass
// their stable catalogue key so the corresponding option and specification
// fields are ready when the visitor arrives. Standalone commercial areas use
// `area` because they are not necessarily rows in the public service catalogue.
export function inquiryPath(
  locale: string,
  options: InquiryLinkOptions = {},
): string {
  const params = new URLSearchParams();
  if (options.serviceKey?.trim()) {
    params.set("service", options.serviceKey.trim());
  }
  if (options.offerVariant) params.set("version", options.offerVariant);
  if (options.area) params.set("area", options.area);
  if (options.requestType?.trim()) {
    params.set("request", options.requestType.trim());
  }
  const query = params.toString();
  return `${localizedPath("inquiries", locale)}${query ? `?${query}` : ""}#quote-form`;
}

// Resolve a slug (from the URL) back to a route key for the current locale.
export function routeKeyFromSlug(
  slug: string,
  locale: string,
): RouteKey | null {
  const loc = (SUPPORTED_LOCALES_ARRAY as string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  for (const key of Object.keys(ROUTE_SLUGS) as RouteKey[]) {
    if (ROUTE_SLUGS[key][loc] === slug) return key;
  }
  return null;
}

// Build a localized href for a specific service detail page.
export function servicePath(service: ServiceKey, locale: string): string {
  const loc = (SUPPORTED_LOCALES_ARRAY as string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  return `${localizedPath("services", loc)}/${SERVICE_SLUGS[service][loc]}`;
}

// Same-origin, cached and optimized artwork for an aggregated magazine item.
export function magazineImagePath(source: string, externalId: number): string {
  const safeSource = source
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
  const safeId =
    Number.isSafeInteger(externalId) && externalId > 0 ? externalId : 0;
  return `/media/magazine/${encodeURIComponent(safeSource)}/${safeId}.webp`;
}

export function portfolioProjectPath(slug: string, locale: string): string {
  return `${localizedPath("portfolio", locale)}/${encodeURIComponent(slug.trim())}`;
}

export function portfolioProjectAlternates(slug: string): LocaleAlternate[] {
  return SUPPORTED_LOCALES_ARRAY.map((locale) => ({
    locale,
    path: portfolioProjectPath(slug, locale),
  }));
}

// Resolve a service sub-slug (from the URL) back to a service key, trying every
// locale so a permalink still resolves if the visitor switches language.
export function serviceKeyFromSlug(slug: string): ServiceKey | null {
  for (const key of SERVICE_KEYS) {
    for (const loc of SUPPORTED_LOCALES_ARRAY) {
      if (SERVICE_SLUGS[key][loc] === slug) return key;
    }
  }
  return null;
}

// Kept only for compatibility routes from the former nested forum URLs.
export const FORUM_SUBPATH = "forum";

export const FORUM_CATEGORY_SLUGS = {
  websites: {
    it: "siti-web",
    en: "websites",
    es: "sitios-web",
    fr: "sites-web",
    de: "webseiten",
  },
  ecommerce: {
    it: "e-commerce",
    en: "e-commerce",
    es: "e-commerce",
    fr: "e-commerce",
    de: "e-commerce",
  },
  webapp: {
    it: "web-app",
    en: "web-apps",
    es: "aplicaciones-web",
    fr: "applications-web",
    de: "web-apps",
  },
  plugin: {
    it: "plugin-moduli",
    en: "plugins-modules",
    es: "plugins-modulos",
    fr: "plugins-modules",
    de: "plugins-module",
  },
  hosting: {
    it: "hosting",
    en: "hosting",
    es: "hosting",
    fr: "hebergement",
    de: "hosting",
  },
  seo: { it: "seo", en: "seo", es: "seo", fr: "seo", de: "seo" },
  social: {
    it: "social-media",
    en: "social-media",
    es: "redes-sociales",
    fr: "reseaux-sociaux",
    de: "social-media",
  },
  campaigns: {
    it: "campagne-pubblicitarie",
    en: "advertising-campaigns",
    es: "campanas-publicitarias",
    fr: "campagnes-publicitaires",
    de: "werbekampagnen",
  },
  graphics: {
    it: "grafica",
    en: "graphic-design",
    es: "diseno-grafico",
    fr: "design-graphique",
    de: "grafikdesign",
  },
  pc: {
    it: "computer",
    en: "computers",
    es: "ordenadores",
    fr: "ordinateurs",
    de: "computer",
  },
  smartphone: {
    it: "smartphone",
    en: "smartphones",
    es: "smartphones",
    fr: "smartphones",
    de: "smartphones",
  },
  fiber: {
    it: "fibra",
    en: "fibre",
    es: "fibra",
    fr: "fibre",
    de: "glasfaser",
  },
  mobile_sim: {
    it: "mobile-sim",
    en: "mobile-sim",
    es: "movil-sim",
    fr: "mobile-sim",
    de: "mobilfunk-sim",
  },
  partner_carriers: {
    it: "operatori-partner",
    en: "partner-carriers",
    es: "operadores-asociados",
    fr: "operateurs-partenaires",
    de: "partneranbieter",
  },
  administration_payments: {
    it: "amministrazione-pagamenti",
    en: "administration-payments",
    es: "administracion-pagos",
    fr: "administration-paiements",
    de: "verwaltung-zahlungen",
  },
  other: {
    it: "altro",
    en: "other",
    es: "otros",
    fr: "autres",
    de: "sonstiges",
  },
} as const satisfies Record<string, Record<SupportedLocale, string>>;

export type ForumCategoryKey = keyof typeof FORUM_CATEGORY_SLUGS;

export function forumPath(locale: string): string {
  return localizedPath("contact", locale);
}

export function forumCategoryPath(
  category: ForumCategoryKey,
  locale: string,
): string {
  const loc = (SUPPORTED_LOCALES_ARRAY as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  return `${forumPath(loc)}/categoria/${FORUM_CATEGORY_SLUGS[category][loc]}`;
}

export function forumCategoryFromSlug(
  slug: string,
  locale: string,
): ForumCategoryKey | null {
  const loc = (SUPPORTED_LOCALES_ARRAY as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  return (
    (Object.keys(FORUM_CATEGORY_SLUGS) as ForumCategoryKey[]).find(
      (category) => FORUM_CATEGORY_SLUGS[category][loc] === slug,
    ) ?? null
  );
}

export function forumPostPath(
  id: string,
  locale: string,
  category?: string,
): string {
  if (category && category in FORUM_CATEGORY_SLUGS) {
    return `${forumCategoryPath(category as ForumCategoryKey, locale)}/${id}`;
  }
  return `${forumPath(locale)}/discussione/${id}`;
}

// Build a localized href for a magazine article detail page.
// Articles are keyed by their source + WordPress post id.
export function articlePath(
  source: string,
  externalId: number,
  locale: string,
): string {
  return `${localizedPath("blog", locale)}/${source}/${externalId}`;
}

export function magazineFeedPath(locale: string): string {
  return `${localizedPath("blog", locale)}/feed.xml`;
}

export function magazineAtomPath(locale: string): string {
  return `${localizedPath("blog", locale)}/atom.xml`;
}

// Build a localized href for a client shop detail page. Client shops live under
// the shop route, keyed by the portfolio project slug (e.g. /it/negozio/arinani-gioielli).
export function clientShopPath(slug: string, locale: string): string {
  return `${localizedPath("shop", locale)}/${slug}`;
}

export type LocaleAlternate = { locale: SupportedLocale; path: string };

export const ENERGY_COMPARATOR_SLUGS: Record<SupportedLocale, string> = {
  it: "comparatore-energia",
  en: "energy-comparator",
  es: "comparador-energia",
  fr: "comparateur-energie",
  de: "energievergleich",
};

export function energyComparatorPath(locale: string): string {
  const loc = (SUPPORTED_LOCALES_ARRAY as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : "it";
  return `${localizedPath("tools", loc)}/${ENERGY_COMPARATOR_SLUGS[loc]}`;
}

export function energyComparatorAlternates(): LocaleAlternate[] {
  return SUPPORTED_LOCALES_ARRAY.map((locale) => ({
    locale,
    path: energyComparatorPath(locale),
  }));
}

// Build the full set of localized permalinks for a route key, one per language.
// Used for <link rel="alternate" hreflang> tags and the sitemap.
export function localizedAlternates(key: RouteKey): LocaleAlternate[] {
  return SUPPORTED_LOCALES_ARRAY.map((loc) => ({
    locale: loc,
    path: localizedPath(key, loc),
  }));
}

// Same as localizedAlternates but for a specific service detail page.
export function serviceAlternates(service: ServiceKey): LocaleAlternate[] {
  return SUPPORTED_LOCALES_ARRAY.map((loc) => ({
    locale: loc,
    path: servicePath(service, loc),
  }));
}

// Same as localizedAlternates but for a specific client shop detail page.
export function clientShopAlternates(slug: string): LocaleAlternate[] {
  return SUPPORTED_LOCALES_ARRAY.map((loc) => ({
    locale: loc,
    path: clientShopPath(slug, loc),
  }));
}

// Every indexable route key (includes the legal pages now that they are real).
export const INDEXABLE_ROUTE_KEYS: RouteKey[] = [
  "home",
  "about",
  "careers",
  "services",
  "tools",
  "editorial",
  "sponsored",
  "portfolio",
  "partners",
  "blog",
  "shop",
  "contact",
  "faq",
  "inquiries",
  "privacy",
  "cookie",
  "terms",
  "withdrawal",
];
